/**
 * `iconChip` — a bordered square chip with a stroked VECTOR icon, for
 * on-canvas chip controls (the position plan's + / ×).
 *
 * Why not a text figure: glyph characters proved impossible to center
 * reliably — ＋/✕ style codepoints fall back to different system fonts per
 * platform, and even native +/× ink floats inside the em box (fonts reserve
 * descent space the glyphs never use), so every size tweak re-broke optical
 * centering. A Path2D stroked from lucide's 24×24 path data centers
 * geometrically — no font metrics involved — and matches the DOM UI's
 * lucide-react icons stroke for stroke.
 *
 * Path data from lucide (https://lucide.dev), ISC license.
 */
import { registerFigure } from 'klinecharts'
import { registerOnce } from './registrationUtils'

export const ICON_CHIP_FIGURE = 'iconChip'

/** lucide `plus` */
export const LUCIDE_PLUS_PATHS = ['M5 12h14', 'M12 5v14']
/** lucide `x` */
export const LUCIDE_X_PATHS = ['M18 6 6 18', 'm6 6 12 12']
/** lucide `bell` (lucide-react 0.576.0) */
export const LUCIDE_BELL_PATHS = [
  'M10.268 21a2 2 0 0 0 3.464 0',
  'M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326',
]
/** lucide `rocket` */
export const LUCIDE_ROCKET_PATHS = [
  'M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z',
  'm12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z',
  'M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0',
  'M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5',
]

export interface IconChipAttrs {
  /** Left edge of the chip box. */
  x: number
  /** VERTICAL CENTER of the chip box (matches the label pills' middle
   *  baseline so a row of pill + chips shares one centerline). */
  y: number
  /** Box edge length — the chip is square. */
  size: number
  /** Icon path data in lucide's 24×24 viewBox coordinates. */
  paths: string[]
}

export interface IconChipStyles {
  /** Icon stroke AND border color (the chip's accent). */
  color?: string
  backgroundColor?: string
  borderRadius?: number
  /** Icon ink edge within the box, px. Default: box minus 3px each side. */
  iconSize?: number
  /** Stroke width in lucide viewBox units (lucide default is 2). */
  strokeWidth?: number
  /** Draw the chip's square border (default true). Off for a bare glyph
   *  that marks something rather than inviting a click. */
  border?: boolean
}

const LUCIDE_VIEWBOX = 24

/** Register the figure with klinecharts (once). */
export const registerIconChipFigure = registerOnce(() => {
  registerFigure({
    name: ICON_CHIP_FIGURE,
    checkEventOn: (coordinate: { x: number; y: number }, attrs: unknown) => {
      const a = attrs as IconChipAttrs
      return (
        coordinate.x >= a.x &&
        coordinate.x <= a.x + a.size &&
        coordinate.y >= a.y - a.size / 2 &&
        coordinate.y <= a.y + a.size / 2
      )
    },
    draw: (ctx: CanvasRenderingContext2D, attrs: unknown, styles: unknown) => {
      const a = attrs as IconChipAttrs
      const s = (styles ?? {}) as IconChipStyles
      const accent = s.color ?? '#787b86'
      const top = a.y - a.size / 2
      const radius = s.borderRadius ?? 4

      // Box — 0.5 offset keeps the 1px border on pixel centers (crisp).
      if (s.border !== false || s.backgroundColor) {
        ctx.beginPath()
        const roundRect = (
          ctx as CanvasRenderingContext2D & {
            roundRect?: (x: number, y: number, w: number, h: number, r: number) => void
          }
        ).roundRect
        if (roundRect) roundRect.call(ctx, a.x + 0.5, top + 0.5, a.size - 1, a.size - 1, radius)
        else ctx.rect(a.x + 0.5, top + 0.5, a.size - 1, a.size - 1)
        ctx.closePath()
        if (s.backgroundColor) {
          ctx.fillStyle = s.backgroundColor
          ctx.fill()
        }
        if (s.border !== false) {
          ctx.strokeStyle = accent
          ctx.lineWidth = 1
          ctx.setLineDash([])
          ctx.stroke()
        }
      }

      // Icon — scaled from the 24×24 viewBox, geometrically centered.
      const iconSize = s.iconSize ?? a.size - 6
      const scale = iconSize / LUCIDE_VIEWBOX
      ctx.save()
      ctx.translate(a.x + (a.size - iconSize) / 2, a.y - iconSize / 2)
      ctx.scale(scale, scale)
      ctx.lineWidth = s.strokeWidth ?? 2
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      for (const d of a.paths) ctx.stroke(new Path2D(d))
      ctx.restore()
    },
  } as Parameters<typeof registerFigure>[0])
})
